import { useState, useCallback, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  convertGammaPercentageToInternalValue,
  convertSaturationPercentageToOffset,
  hasColorAdjustments,
  removeProgrammaticColorAdjustmentsFromImageEdits,
  useProgrammaticImageEdit,
} from '@/hooks/editor/useProgrammaticImageEdit'
import { syncImageEditToEditorPages } from '@/utils/editor/syncImageEditToEditorPages'
import { useUpdatePageItems } from '@/hooks/editor/useUpdatePageItems'
import { isImagePlaceholder } from '@/utils/editor/imagePlaceholder'
import { determineImageUrl } from '@/utils/editor/determineImageUrl'
import type { ImageItem, Item } from '@messenger/types'
import type { QuickActionItem } from '@/types/itemControls'

type UseImageQuickActionsArgs = {
  imgSrc: string | null
  imageEdits: ImageItem['image']['edits']
  url?: string
  /** The accepted cutout the item shows, if any (see ImageItem.image). */
  aiVariantId?: string
  aiVariantUrl?: string
  caseImage?: string | number
  subtype?: string
  filledImages?: string[]
  selected: boolean
  setEditedImageUrl: (url: string | undefined) => void
  setImageEdits: (edits: ImageItem['image']['edits']) => void
  /**
   * Keeps the item's AI provenance when a quick action renders from its
   * cutout, and clears it when one renders from the source photo.
   */
  setAiVariantId: (variantId: string | undefined) => void
  setAiVariantUrl: (url: string | undefined) => void
  setImgSrc: (src: string) => void
}

type UseImageQuickActionsResult = {
  showQuickActions: boolean
  setShowQuickActions: React.Dispatch<React.SetStateAction<boolean>>
  isProcessingEdit: boolean
  quickActions: QuickActionItem[]
}

/**
 * Image quick actions hook for programmatic image editing without opening Pintura.
 * On an item that shows an accepted AI cutout, the actions render from the
 * whole cutout, as Pintura does, so they keep the removed background and the
 * "Edited with AI" tag; "Use original photo" in Pintura is the way back.
 */
export const useImageQuickActions = ({
  imgSrc,
  imageEdits,
  url,
  aiVariantId,
  aiVariantUrl,
  caseImage,
  subtype,
  filledImages,
  selected,
  setEditedImageUrl,
  setImageEdits,
  setAiVariantId,
  setAiVariantUrl,
  setImgSrc,
}: UseImageQuickActionsArgs): UseImageQuickActionsResult => {
  const { t } = useTranslation()
  const [showQuickActions, setShowQuickActions] = useState(false)
  const [isProcessingEdit, setIsProcessingEdit] = useState(false)

  // Use useUpdatePageItems to get synchronous items sync for immediate updates
  const { setEditorPagesItems, syncItemsToEditorPages } =
    useUpdatePageItems() as unknown as {
      setEditorPagesItems: (items: Item[]) => void
      syncItemsToEditorPages: () => Item[]
    }

  const { applyImageEdit } = useProgrammaticImageEdit()

  const determineOriginalImageSource = useCallback(() => {
    const originalImageUrl = determineImageUrl({
      url,
      editedUrl: undefined,
      subtype,
      caseImage,
      filledImages,
      logo: undefined,
      fhLogo: undefined,
    })

    return originalImageUrl || ''
  }, [url, caseImage, subtype, filledImages])

  // The cutout counts only with both its id and its stored file.
  const cutoutVariantId = aiVariantId && aiVariantUrl ? aiVariantId : undefined
  const cutoutUrl = cutoutVariantId ? aiVariantUrl : undefined

  /**
   * What a quick action renders from: the item's cutout while it shows one
   * (read at the photo's size), else the source photo.
   */
  const determineEditSource = useCallback(() => {
    const photoUrl = determineOriginalImageSource()
    return cutoutUrl && photoUrl
      ? { imageUrl: cutoutUrl, cutoutOfPhotoUrl: photoUrl }
      : { imageUrl: photoUrl }
  }, [determineOriginalImageSource, cutoutUrl])

  // Close quick actions when image is deselected
  useEffect(() => {
    const closeQuickActionsWhenImageIsDeselected = !selected && showQuickActions
    if (closeQuickActionsWhenImageIsDeselected) {
      setShowQuickActions(false)
    }
  }, [selected, showQuickActions])

  /**
   * Handles the result of an image edit operation.
   * Updates the image URL and edits state if the operation was successful.
   */
  const handleImageEditResult = useCallback(
    (result: Awaited<ReturnType<typeof applyImageEdit>>) => {
      if (!result.success) return

      syncImageEditToEditorPages({
        editedUrl: result.editedUrl,
        imageEdits: result.imageState,
        aiVariantId: cutoutVariantId,
        aiVariantUrl: cutoutUrl,
        setEditorPagesItems,
        setEditedImageUrl,
        setImageEdits,
        setAiVariantId,
        setAiVariantUrl,
        syncItemsToEditorPages,
      })

      setImgSrc(result.editedUrl)
    },
    [
      cutoutVariantId,
      cutoutUrl,
      setEditorPagesItems,
      setEditedImageUrl,
      setImageEdits,
      setAiVariantId,
      setAiVariantUrl,
      setImgSrc,
      syncItemsToEditorPages,
    ],
  )

  const handleLighteningImage = useCallback(async () => {
    if (!imgSrc) return

    setShowQuickActions(false)
    setIsProcessingEdit(true)

    try {
      const source = determineEditSource()

      // Apply preset: Gamma 10, Contrast 30
      // Gamma: Convert percentage (10) to Pintura's internal gamma value
      const desiredGammaPercentage = 10
      const convertedGammaValue = convertGammaPercentageToInternalValue(
        desiredGammaPercentage,
      )

      // Contrast: Set to 0.15 (represents Contrast level 30 in the preset spec)
      // Note: The contrast value 0.15 produces the desired visual effect matching
      // the "Contrast 30" preset specification when combined with Gamma 10
      const contrastValue = 0.15

      const sanitizedExistingEdits =
        removeProgrammaticColorAdjustmentsFromImageEdits(imageEdits)

      const result = await applyImageEdit({
        ...source,
        existingEdits: sanitizedExistingEdits,
        edits: {
          gamma: convertedGammaValue,
          contrast: contrastValue,
        },
      })

      handleImageEditResult(result)
    } finally {
      setIsProcessingEdit(false)
    }
  }, [
    applyImageEdit,
    imgSrc,
    imageEdits,
    determineEditSource,
    handleImageEditResult,
  ])

  const handleRednessImage = useCallback(async () => {
    if (!imgSrc) return

    setShowQuickActions(false)
    setIsProcessingEdit(true)

    try {
      const source = determineEditSource()

      // Apply preset: Gamma 10, Contrast 30, Saturation -15
      // Gamma: Convert percentage (10) to Pintura's internal gamma value
      const desiredGammaPercentage = 10
      const convertedGammaValue = convertGammaPercentageToInternalValue(
        desiredGammaPercentage,
      )

      // Contrast: Set to 0.15 (represents Contrast level 30 in the preset spec)
      // Note: The contrast value 0.15 produces the desired visual effect matching
      // the "Contrast 30" preset specification when combined with Gamma 15
      const contrastValue = 0.15

      // Saturation: Convert percentage (-15) to Pintura's internal saturation offset
      const desiredSaturationPercentage = -15
      const convertedSaturationOffset = convertSaturationPercentageToOffset(
        desiredSaturationPercentage,
      )

      const sanitizedExistingEdits =
        removeProgrammaticColorAdjustmentsFromImageEdits(imageEdits)

      const result = await applyImageEdit({
        ...source,
        existingEdits: sanitizedExistingEdits,
        edits: {
          gamma: convertedGammaValue,
          contrast: contrastValue,
          saturation: convertedSaturationOffset,
        },
      })

      handleImageEditResult(result)
    } finally {
      setIsProcessingEdit(false)
    }
  }, [
    applyImageEdit,
    imgSrc,
    imageEdits,
    determineEditSource,
    handleImageEditResult,
  ])

  const handleResetAdjustments = useCallback(async () => {
    if (!imgSrc) return

    setShowQuickActions(false)
    setIsProcessingEdit(true)

    try {
      const source = determineEditSource()

      if (!source.imageUrl) {
        console.warn(
          'Cannot reset adjustments: original image source URL is missing',
        )
        return
      }

      const result = await applyImageEdit({
        ...source,
        existingEdits: {},
        edits: {
          reset: true,
        },
      })

      handleImageEditResult(result)
    } finally {
      setIsProcessingEdit(false)
    }
  }, [applyImageEdit, imgSrc, determineEditSource, handleImageEditResult])

  const quickActions = useMemo<QuickActionItem[]>(() => {
    const isPlaceholder = isImagePlaceholder(imgSrc || '')
    const hasAdjustments = hasColorAdjustments(imageEdits)

    return [
      {
        id: 'lightening',
        label: t('editor.pageItems.pageImage.quickActions.lighteningImage'),
        onAction: handleLighteningImage,
        disabled: isPlaceholder || isProcessingEdit,
      },
      {
        id: 'redness',
        label: t('editor.pageItems.pageImage.quickActions.rednessImage'),
        onAction: handleRednessImage,
        disabled: isPlaceholder || isProcessingEdit,
      },
      {
        id: 'reset',
        label: t('editor.pageItems.pageImage.quickActions.resetAdjustments'),
        onAction: handleResetAdjustments,
        disabled: isPlaceholder || isProcessingEdit || !hasAdjustments,
      },
    ]
  }, [
    handleLighteningImage,
    handleRednessImage,
    handleResetAdjustments,
    imgSrc,
    imageEdits,
    isProcessingEdit,
    t,
  ])

  return {
    showQuickActions,
    setShowQuickActions,
    isProcessingEdit,
    quickActions,
  }
}
